/**
 * Live prefixes store a Minecraft text component in `prefix_json`.
 *
 * Gradient prefixes give every letter its own run ("Pureblood" is nine runs)
 * and some wrap their runs in `extra`, so the internal `name`
 * ("vampiresevent") is often nothing like what players see. Pickers search
 * the in-game text as well as the name.
 */
import type { Prefix } from '../types/eventTeam';

export interface PrefixRun {
  text: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underlined?: boolean;
  strikethrough?: boolean;
}

type PrefixStyle = Omit<PrefixRun, 'text'>;

const FORMAT_KEYS = ['bold', 'italic', 'underlined', 'strikethrough'] as const;

function styleOf(node: Record<string, unknown>, inherited: PrefixStyle): PrefixStyle {
  const style = { ...inherited };
  if (typeof node.color === 'string') style.color = node.color;
  for (const key of FORMAT_KEYS) if (typeof node[key] === 'boolean') style[key] = node[key] as boolean;
  return style;
}

const isComponent = (node: unknown): node is Record<string, unknown> =>
  typeof node === 'object' && node !== null && !Array.isArray(node);

/** Children inherit their parent's style, as in game. A list is its first entry with the rest as `extra`. */
function flatten(node: unknown, inherited: PrefixStyle): PrefixRun[] {
  if (typeof node === 'string') return [{ ...inherited, text: node }];
  if (Array.isArray(node)) {
    const [first, ...rest] = node;
    const style = isComponent(first) ? styleOf(first, inherited) : inherited;
    return [...flatten(first, inherited), ...rest.flatMap((child) => flatten(child, style))];
  }
  if (!isComponent(node)) return [];
  const style = styleOf(node, inherited);
  const own = typeof node.text === 'string' ? [{ ...style, text: node.text }] : [];
  const extra = Array.isArray(node.extra) ? node.extra.flatMap((child) => flatten(child, style)) : [];
  return [...own, ...extra];
}

/** Styled runs to render, or `null` when there is no readable in-game text. */
export function prefixRuns(prefixJson: string | null): PrefixRun[] | null {
  if (!prefixJson) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(prefixJson);
  } catch {
    return null;
  }
  const runs = flatten(parsed, {}).filter((run) => run.text !== '');
  return runs.length > 0 ? runs : null;
}

/** What the prefix says in game, e.g. `[Pureblood]`. */
export function prefixPlainText(prefixJson: string | null): string {
  return (prefixRuns(prefixJson) ?? []).map((run) => run.text).join('');
}

// Internal names squash words together ("vampiresevent", "partner_pixeldream").
const normalise = (value: string) => value.toLowerCase().replace(/[\s_-]+/g, '');

/** Case-insensitive match on a prefix's in-game text, internal name or category. */
export function prefixMatches(prefix: Pick<Prefix, 'name' | 'category' | 'prefix_json'>, search: string): boolean {
  const needle = normalise(search);
  if (!needle) return true;
  return [prefixPlainText(prefix.prefix_json), prefix.name, prefix.category].some((value) => normalise(value).includes(needle));
}
